import { ElmLitElement } from "../../Aspect/ElmLitElement";
import { property } from "lit-element";
import {Placement, arrow, computePosition, flip, offset, shift, autoUpdate} from "@floating-ui/dom";

export {
  defineCustomTooltip,
};

const defineCustomTooltip = (): void => customElements.define("custom-tooltip", CustomTooltip);

const tooltipVisibleAttribute = "data-visible";

class CustomTooltip extends ElmLitElement {
  @property({ type: Number, attribute: "timeout" }) timeout = 3000;
  @property({ type: String, attribute: "tooltip-prefers" }) tooltipPrefers: Placement = "bottom";

  private abortController?: AbortController;
  private cleanup?: () => void;

  connectedCallback(): void {
    super.connectedCallback();
    this.abortController = new AbortController();

    this.addEventListener("mouseenter", this.showTooltip);
    this.addEventListener("mouseleave", this.hideTooltip);

    if (process.env.isHybrid) {
      this.addEventListener("pointerdown", (): void => {
        this.showTooltip();
        this.addEventListener("pointerup", (): void => {
          const abortClickAway = new AbortController();
          window.setTimeout((): void => {
              this.hideTooltip();
              abortClickAway.abort();
            },
            this.timeout
          );
          document.body.addEventListener("pointerdown", this.hideTooltip, {
            once: true,
            signal: abortClickAway.signal
          });
        }, {
          once: true
        });
      });
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.abortController && this.abortController.abort();
    this.cleanup && this.cleanup();
  }

  showTooltip(): void {
    const tooltip = mapMaybe(toMaybeHtmlElement, getTooltipByTagName(this));
    const arrowEl = mapMaybe(toMaybeHtmlElement, getArrowByTagName(this));
    if (tooltip) {
      tooltip.setAttribute(tooltipVisibleAttribute, "");
      this.cleanup = autoUpdate(
        this,
        tooltip,
        () => updatePosition(this, tooltip, arrowEl),
      );
    }
  }

  hideTooltip(): void {
    const tooltip = mapMaybe(toMaybeHtmlElement, getTooltipByTagName(this));
    tooltip?.removeAttribute(tooltipVisibleAttribute);
    this.cleanup && this.cleanup();
  }
}

//
function updatePosition(component: CustomTooltip, tooltip: HTMLElement, arrowEl: HTMLElement | null): void {
  computePosition(component, tooltip, {
    placement: component.tooltipPrefers,
    middleware: [
      ...(arrowEl ? [ offset(state => {
        if (state.placement.startsWith("top") || state.placement.startsWith("bottom")) {
          return arrowEl.offsetWidth / 2;
        }
        return arrowEl.offsetHeight / 2;
      })] : []),
      flip(),
      shift(),
      ...(arrowEl ? [ arrow({ element: arrowEl, padding: arrowEl.offsetWidth / 2 }) ] : [])
    ],
  }).then(({ x, y, placement, middlewareData }): void => {
    Object.assign(tooltip.style, { left: `${x}px`, top: `${y}px`});

    if (arrowEl instanceof HTMLElement && middlewareData.arrow) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow;
      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
      }[placement.split("-")[0]];

      Object.assign(arrowEl.style, {
        left: arrowX !== undefined ? `${arrowX}px` : "",
        top: arrowY !== undefined ? `${arrowY}px` : "",
        right: "",
        bottom: "",
        ...(staticSide ? { [staticSide] : `-${(arrowEl.offsetWidth / 2).toFixed()}px` } : {}),
      })
    }
  });
}

//
function mapMaybe<T, U>(callback: (value: T) => U | null, value: T | null): U | null {
  return value !== null ? callback(value) : null;
}
function toMaybeHtmlElement(element: Element): HTMLElement | null {
  return element instanceof HTMLElement ? element : null;
}

function getTooltipByTagName(thisEl: CustomTooltip): Element | null { return thisEl.querySelector("tooltip-content"); }
function getArrowByTagName(thisEl: CustomTooltip): Element | null { return thisEl.querySelector("tooltip-arrow"); }
